import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { indexDailyLogRow } from "./knowledge";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";

export type SiteDailyLog = Tables<"site_daily_logs">;
export type SiteDailyLogInsert = Insert<"site_daily_logs">;
export type SiteDailyLogUpdate = Update<"site_daily_logs">;

function scheduleDailyLogIndex(row: SiteDailyLog): void {
  void indexDailyLogRow(row).catch(() => {
    // Indexelés best-effort — a mentés már sikerült.
  });
}

export async function listSiteDailyLogs(): Promise<SiteDailyLog[]> {
  const { data, error } = await supabase.from("site_daily_logs").select("*").order("log_date", { ascending: false });
  throwIfError(error);
  return excludeTestData((data ?? []) as Array<SiteDailyLog & { is_test?: boolean | null }>);
}

export async function getSiteDailyLogByDate(logDate: string): Promise<SiteDailyLog | null> {
  const { data, error } = await supabase.from("site_daily_logs").select("*").eq("log_date", logDate).maybeSingle();
  throwIfError(error);
  return data ?? null;
}

export async function createSiteDailyLog(
  values: SiteDailyLogInsert,
  source: WriteSource = "ui"
): Promise<SiteDailyLog> {
  const row = await insertWithWriteSource<SiteDailyLog>(
    "site_daily_logs",
    values as unknown as Record<string, unknown>,
    source
  );
  scheduleDailyLogIndex(row);
  return row;
}

export async function updateSiteDailyLog(id: string, values: SiteDailyLogUpdate): Promise<SiteDailyLog> {
  const existing = await supabase.from("site_daily_logs").select("locked_at, issues").eq("id", id).maybeSingle();
  if (existing.data?.locked_at) {
    // F4.4: zárolt nap — csak issues (kiegészítő megjegyzés) bővíthető
    const onlyIssues = Object.keys(values).every((k) => k === "issues");
    if (!onlyIssues) {
      throw new Error("Zárolt napló nap — csak kiegészítő megjegyzés adható hozzá.");
    }
    const prev = existing.data.issues ?? "";
    const next = values.issues ?? "";
    if (next && prev && !next.startsWith(prev)) {
      values = { issues: `${prev}\n---\n${next}` };
    }
  }
  const { data, error } = await supabase.from("site_daily_logs").update(values).eq("id", id).select("*").single();
  throwIfError(error);
  const row = assertData(data, "Napló bejegyzés nem található.");
  scheduleDailyLogIndex(row);
  return row;
}

/** F4.4 — napi zárolás. */
export async function lockSiteDailyLog(id: string): Promise<SiteDailyLog> {
  const { data, error } = await supabase
    .from("site_daily_logs")
    .update({ locked_at: new Date().toISOString() })
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  return assertData(data, "Napló bejegyzés nem található.");
}

export async function upsertSiteDailyLogByDate(values: SiteDailyLogInsert): Promise<SiteDailyLog> {
  const { data, error } = await supabase
    .from("site_daily_logs")
    .upsert(values, { onConflict: "log_date" })
    .select("*")
    .single();
  throwIfError(error);
  const row = assertData(data);
  scheduleDailyLogIndex(row);
  return row;
}

/**
 * A mai naplóhoz sosem felülírással írunk — a chat / fotó javaslatok appendelnek,
 * hogy egy AI-javaslat ne törölje a kézzel írt sorokat.
 */
export async function appendToSiteDailyLog(args: {
  logDate: string;
  workSummary?: string;
  issues?: string;
  weatherSummary?: string;
  taskId?: string | null;
}): Promise<SiteDailyLog> {
  const existing = await getSiteDailyLogByDate(args.logDate);
  const join = (current: string | null | undefined, addition?: string): string | null => {
    const add = addition?.trim();
    const cur = (current ?? "").trim();
    if (!add) return current ?? null;
    if (!cur) return add;
    if (cur.includes(add)) return cur;
    return `${cur}\n${add}`;
  };

  if (!existing) {
    return createSiteDailyLog({
      log_date: args.logDate,
      work_summary: args.workSummary?.trim() || null,
      issues: args.issues?.trim() || null,
      weather_summary: args.weatherSummary?.trim() || null,
      task_id: args.taskId ?? null
    });
  }

  return updateSiteDailyLog(existing.id, {
    work_summary: join(existing.work_summary, args.workSummary),
    issues: join(existing.issues, args.issues),
    weather_summary: existing.weather_summary ?? args.weatherSummary?.trim() ?? null,
    task_id: existing.task_id ?? args.taskId ?? null
  });
}

export const siteDailyLogsQueryOptions = () =>
  queryOptions({
    queryKey: qk.siteDailyLogs,
    queryFn: listSiteDailyLogs
  });
